import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  SidenavItem,
  SidenavGroup,
  SidenavState,
} from '../models/sidenav.interface';
import { UserType } from '../enums/sidenav.enum';
import { LocalStorageService } from '../../services/localStorage.service';

@Injectable({
  providedIn: 'root',
})
export class SidenavGroupsService {
  private readonly _groupsExpanded = new BehaviorSubject<
    Record<string, boolean>
  >({});
  private readonly _groups = new BehaviorSubject<SidenavGroup[]>([]);
  private readonly STORAGE_KEY = 'sidenavGroupsExpanded';

  constructor(private localStorage: LocalStorageService) {
    this.initializeGroups();
    this.loadExpandedStateFromStorage();
  }

  get groups$(): Observable<SidenavGroup[]> {
    return this._groups.asObservable();
  }

  get groupsExpanded$(): Observable<Record<string, boolean>> {
    return this._groupsExpanded.asObservable();
  }

  /**
   * Get filtered groups based on user type and current state
   */
  getFilteredGroups$(
    userType: UserType,
    isCollapsed: boolean,
    activeRoute: string,
    searchTerm?: string,
    getNotificationCount?: (item: SidenavItem) => number
  ): Observable<SidenavGroup[]> {
    return combineLatest([this.groups$, this.groupsExpanded$]).pipe(
      map(([groups, expanded]) => {
        return groups
          .map((group) => this.filterGroupForUser(group, userType))
          .filter((group) => group?.items?.length > 0)
          .map((group) =>
            this.processGroup(
              group,
              expanded,
              isCollapsed,
              activeRoute,
              searchTerm,
              getNotificationCount
            )
          )
          .filter(
            (group) => !searchTerm || this.groupMatchesSearch(group, searchTerm)
          );
      })
    );
  }

  /**
   * Toggle group expansion state
   */
  toggleGroup(groupId: string): void {
    const current = this._groupsExpanded.value;
    const newState = {
      ...current,
      [groupId]: !current[groupId],
    };
    this._groupsExpanded.next(newState);
    this.saveExpandedStateToStorage(newState);
  }

  /**
   * Set group expansion state
   */
  setGroupExpanded(groupId: string, expanded: boolean): void {
    const current = this._groupsExpanded.value;
    const newState = {
      ...current,
      [groupId]: expanded,
    };
    this._groupsExpanded.next(newState);
    this.saveExpandedStateToStorage(newState);
  }

  /**
   * Expand all groups
   */
  expandAllGroups(): void {
    const groups = this._groups.value;
    const expanded = groups.reduce((acc, group) => {
      acc[group.id] = true;
      return acc;
    }, {} as Record<string, boolean>);
    this._groupsExpanded.next(expanded);
    this.saveExpandedStateToStorage(expanded);
  }

  /**
   * Collapse all groups
   */
  collapseAllGroups(): void {
    const groups = this._groups.value;
    const expanded = groups.reduce((acc, group) => {
      acc[group.id] = false;
      return acc;
    }, {} as Record<string, boolean>);
    this._groupsExpanded.next(expanded);
    this.saveExpandedStateToStorage(expanded);
  }

  /**
   * Update groups configuration
   */
  updateGroups(groups: SidenavGroup[]): void {
    this._groups.next(groups);
    this.initializeGroupExpansion(groups, true);
  }

  /**
   * Get group by ID
   */
  getGroup(groupId: string): SidenavGroup | undefined {
    return this._groups.value.find((group) => group.id === groupId);
  }

  /**
   * Find group containing a specific item
   */
  findGroupByItem(itemId: string): SidenavGroup | undefined {
    return this._groups.value.find((group) =>
      group.items.some((item) => item.id === itemId)
    );
  }

  private initializeGroups(): void {
    const defaultGroups: SidenavGroup[] = [
      {
        id: 'core',
        label: 'Core Features',
        icon: 'home',
        order: 1,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: 'dashboard',
            label: 'Dashboard',
            route: '/dashboard',
            icon: 'assets/icons/dashboard.svg',
            order: 1,
            isVisible: true,
            userTypes: [UserType.HQ, UserType.POS, UserType.KIOSK],
          },
          {
            id: 'sales',
            label: 'Sales',
            route: '/sales',
            icon: 'assets/icons/sales.svg',
            order: 2,
            isVisible: true,
            userTypes: [UserType.HQ, UserType.POS],
          },
          {
            id: 'customers',
            label: 'Customers',
            route: '/customers',
            icon: 'assets/icons/customers.svg',
            order: 3,
            isVisible: true,
            userTypes: [UserType.HQ, UserType.POS],
          },
        ],
      },
      {
        id: 'inventory',
        label: 'Inventory Management',
        icon: 'database',
        order: 2,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: 'products',
            label: 'Products',
            route: '/inventory/products',
            icon: 'assets/icons/products.svg',
            order: 1,
            isVisible: true,
            userTypes: [UserType.HQ, UserType.POS],
          },
          {
            id: 'stock',
            label: 'Stock Management',
            route: '/inventory/stock',
            icon: 'assets/icons/stock.svg',
            order: 2,
            isVisible: true,
            userTypes: [UserType.HQ],
            requiredFeatures: ['inventory_management'],
          },
          {
            id: 'suppliers',
            label: 'Suppliers',
            route: '/inventory/suppliers',
            icon: 'assets/icons/suppliers.svg',
            order: 3,
            isVisible: true,
            userTypes: [UserType.HQ],
            requiredFeatures: ['supplier_management'],
          },
        ],
      },
      {
        id: 'analytics',
        label: 'Analytics & Reports',
        icon: 'bar-chart',
        order: 3,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: 'reports',
            label: 'Reports',
            route: '/analytics/reports',
            icon: 'assets/icons/reports.svg',
            order: 1,
            isVisible: true,
            userTypes: [UserType.HQ, UserType.POS],
            requiredFeatures: ['advanced_reporting'],
          },
          {
            id: 'insights',
            label: 'Business Insights',
            route: '/analytics/insights',
            icon: 'assets/icons/insights.svg',
            order: 2,
            isVisible: true,
            userTypes: [UserType.HQ],
            requiredFeatures: ['business_insights'],
          },
        ],
      },
      {
        id: 'settings',
        label: 'Settings & Configuration',
        icon: 'settings',
        order: 4,
        isCollapsible: true,
        isVisible: true,
        items: [
          {
            id: 'account',
            label: 'Account Settings',
            route: '/settings/account',
            icon: 'assets/icons/account.svg',
            order: 1,
            isVisible: true,
            userTypes: [UserType.HQ, UserType.POS, UserType.KIOSK],
          },
          {
            id: 'permissions',
            label: 'User Permissions',
            route: '/settings/permissions',
            icon: 'assets/icons/permissions.svg',
            order: 2,
            isVisible: true,
            userTypes: [UserType.HQ],
            requiredFeatures: ['user_management'],
          },
          {
            id: 'integrations',
            label: 'Integrations',
            route: '/settings/integrations',
            icon: 'assets/icons/integrations.svg',
            order: 3,
            isVisible: true,
            userTypes: [UserType.HQ],
            requiredFeatures: ['third_party_integrations'],
          },
        ],
      },
    ];

    this.updateGroups(defaultGroups);
  }

  private initializeGroupExpansion(
    groups: SidenavGroup[],
    preserveExisting: boolean = false
  ): void {
    const currentExpanded = preserveExisting ? this._groupsExpanded.value : {};
    const expanded = groups.reduce((acc, group) => {
      if (preserveExisting && currentExpanded.hasOwnProperty(group.id)) {
        acc[group.id] = currentExpanded[group.id];
      } else {
        acc[group.id] = group.isExpanded ?? true;
      }
      return acc;
    }, {} as Record<string, boolean>);
    this._groupsExpanded.next(expanded);

    if (preserveExisting) {
      this.saveExpandedStateToStorage(expanded);
    }
  }

  private loadExpandedStateFromStorage(): void {
    try {
      const stored = this.localStorage.get(this.STORAGE_KEY);
      if (stored) {
        const expandedState = JSON.parse(stored);
        this._groupsExpanded.next(expandedState);
      }
    } catch (error) {
      console.warn(
        'Failed to load sidenav group expansion state from storage:',
        error
      );
    }
  }

  private saveExpandedStateToStorage(
    expandedState: Record<string, boolean>
  ): void {
    try {
      this.localStorage.set(this.STORAGE_KEY, JSON.stringify(expandedState));
    } catch (error) {
      console.warn(
        'Failed to save sidenav group expansion state to storage:',
        error
      );
    }
  }

  private filterGroupForUser(
    group: SidenavGroup,
    userType: UserType
  ): SidenavGroup {
    const filteredItems = group.items.filter((item) =>
      this.isItemVisibleForUser(item, userType)
    );

    return {
      ...group,
      items: filteredItems,
    };
  }

  private isItemVisibleForUser(item: SidenavItem, userType: UserType): boolean {
    if (!item.isVisible) return false;
    if (!item.userTypes.includes(userType)) return false;
    return true;
  }

  private processGroup(
    group: SidenavGroup,
    expanded: Record<string, boolean>,
    isCollapsed: boolean,
    activeRoute: string,
    searchTerm?: string,
    getNotificationCount?: (item: SidenavItem) => number
  ): SidenavGroup {
    const isExpanded = expanded[group.id] ?? true;
    const hasActiveItem = group.items.some((item) =>
      this.isItemActive(item, activeRoute)
    );

    let filteredItems = group.items;
    if (searchTerm) {
      filteredItems = group.items.filter((item) =>
        this.itemMatchesSearch(item, searchTerm)
      );
    }

    // Calculate children count (total notification count of all items)
    const childrenCount = getNotificationCount
      ? filteredItems.reduce((total, item) => {
          return total + getNotificationCount(item);
        }, 0)
      : filteredItems.reduce((total, item) => {
          return total + (item.notificationCount || 0);
        }, 0);

    return {
      ...group,
      items: filteredItems,
      isExpanded: isCollapsed ? false : isExpanded,
      hasActiveItem,
      childrenCount,
    };
  }

  private isItemActive(item: SidenavItem, activeRoute: string): boolean {
    if (!item.route || !activeRoute) return false;

    // Strip query parameters and fragments from the active route
    const cleanActiveRoute = activeRoute.split('?')[0].split('#')[0];

    // Remove leading slash for consistent comparison
    const itemRoute = item.route.startsWith('/')
      ? item.route.substring(1)
      : item.route;
    const currentRoute = cleanActiveRoute.startsWith('/')
      ? cleanActiveRoute.substring(1)
      : cleanActiveRoute;

    // Exact match first
    if (currentRoute === itemRoute) return true;

    // Check if current route starts with item route (for sub-routes)
    if (currentRoute.startsWith(itemRoute + '/')) return true;

    // Special cases for complex routes
    if (itemRoute.includes('/') && currentRoute.includes('/')) {
      const itemBasePath = itemRoute.split('/')[0];
      const currentBasePath = currentRoute.split('/')[0];
      return (
        itemBasePath === currentBasePath && currentRoute.includes(itemRoute)
      );
    }

    return false;
  }

  private groupMatchesSearch(group: SidenavGroup, searchTerm: string): boolean {
    const term = searchTerm.toLowerCase();
    return (
      group.label.toLowerCase().includes(term) ||
      group.items.some((item) => this.itemMatchesSearch(item, term))
    );
  }

  private itemMatchesSearch(item: SidenavItem, searchTerm: string): boolean {
    const term = searchTerm.toLowerCase();
    return (
      item.label.toLowerCase().includes(term) ||
      (item.description && item.description.toLowerCase().includes(term)) ||
      (item.keywords &&
        item.keywords.some((keyword) => keyword.toLowerCase().includes(term)))
    );
  }
}
